/**
 * Shared utilities for sales data transformation
 * Ensures consistency between SalesPage and SalesReports
 */

/**
 * Format amount for display
 */
export const formatAmount = (amount) => {
  if (!amount || isNaN(amount)) return '0 DA';
  return `${Math.round(Number(amount)).toLocaleString('fr-FR')} DA`;
};

/**
 * Format date for display
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return new Date().toLocaleDateString('fr-FR');
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR');
};

/**
 * Get entity name from customer object
 */
export const getEntityName = (entity) => {
  if (!entity) return 'Client inconnu';
  
  if (entity.type === 'company' && entity.companyName) {
    return entity.companyName;
  }
  
  if (entity.firstName && entity.lastName) {
    return `${entity.firstName} ${entity.lastName}`;
  }
  
  if (entity.name) {
    return entity.name;
  }
  
  return 'Client inconnu';
};

/**
 * Transform raw sales data to normalized display format
 * This is the SINGLE SOURCE OF TRUTH for sales data transformation
 */
export const transformSalesData = (rawSales, customers = []) => {
  console.log('🔄 Transforming sales data:', {
    rawSalesCount: rawSales?.length || 0,
    customersCount: customers?.length || 0,
    sampleRawSale: rawSales?.[0]
  });

  if (!rawSales || !Array.isArray(rawSales)) {
    console.log('⚠️ No valid sales data to transform');
    return [];
  }

  const transformedSales = rawSales.map((sale) => {
    // Find customer info
    const customer = customers.find(c => c.id === sale.customerId);
    
    // Calculate total amount from items (preferred) or use total
    let amount = 0;
    if (Array.isArray(sale.items) && sale.items.length > 0) {
      amount = sale.items.reduce((sum, item) => {
        const itemTotal = Number(item.total) || (Number(item.quantity || 0) * Number(item.price || 0));
        return sum + itemTotal;
      }, 0);
    } else {
      amount = Number(sale.total || 0);
    }

    const transformedSale = {
      id: sale.id,
      invoiceNumber: sale.invoiceNumber || sale.id,
      customer: sale.customerName || getEntityName(customer),
      customerId: sale.customerId,
      customerName: sale.customerName || getEntityName(customer),
      date: formatDate(sale.date || sale.createdAt),
      rawDate: sale.date || sale.createdAt,
      amount: formatAmount(amount),
      rawAmount: amount,
      status: sale.status || 'completed',
      type: sale.type || 'retail',
      paymentMethod: sale.paymentMethod || 'cash',
      items: sale.items || [],
      notes: sale.notes || '',
      __raw: sale, // Keep original for debugging
    };

    return transformedSale;
  }).sort((a, b) => {
    // Sort by date descending (newest first)
    const dateA = new Date(a.rawDate || Date.now());
    const dateB = new Date(b.rawDate || Date.now());
    return dateB - dateA;
  });

  console.log('✅ Sales data transformed:', {
    transformedCount: transformedSales.length,
    sampleTransformed: transformedSales[0],
    totalAmount: transformedSales.reduce((sum, sale) => sum + sale.rawAmount, 0)
  });

  return transformedSales;
};

/**
 * Calculate sales summary statistics
 */
export const calculateSalesStats = (transformedSales) => {
  if (!transformedSales || !Array.isArray(transformedSales)) {
    return {
      totalSales: 0,
      totalAmount: 0,
      averageOrderValue: 0,
      completedSales: 0,
      pendingSales: 0,
      totalItems: 0
    };
  }

  const totalAmount = transformedSales.reduce((sum, sale) => sum + sale.rawAmount, 0);
  const completedSales = transformedSales.filter(sale => sale.status === 'completed').length;
  const pendingSales = transformedSales.filter(sale => sale.status === 'pending').length;
  const totalItems = transformedSales.reduce((sum, sale) => sum + (sale.items?.length || 0), 0);

  return {
    totalSales: transformedSales.length,
    totalAmount,
    averageOrderValue: transformedSales.length > 0 ? totalAmount / transformedSales.length : 0,
    completedSales,
    pendingSales,
    totalItems
  };
};

/**
 * Group sales by period for charts
 */
export const groupSalesByPeriod = (transformedSales, periodType = 'month') => {
  if (!transformedSales || !Array.isArray(transformedSales)) return [];

  const periods = {};
  
  transformedSales.forEach(sale => {
    const date = new Date(sale.rawDate || Date.now());
    let periodKey;
    
    switch (periodType) {
      case 'day':
        periodKey = date.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        periodKey = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      case 'year':
        periodKey = String(date.getFullYear());
        break;
      default:
        periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    
    if (!periods[periodKey]) {
      periods[periodKey] = {
        period: periodKey,
        sales: [],
        totalAmount: 0,
        totalSales: 0
      };
    }
    
    periods[periodKey].sales.push(sale);
    periods[periodKey].totalAmount += sale.rawAmount;
    periods[periodKey].totalSales += 1;
  });

  return Object.values(periods).sort((a, b) => a.period.localeCompare(b.period));
};